import {
    CreateCustomerPayload,
    GetCustomerStatus,
    JoinCustomerPayload,
    TCreateCustomerUtilReturn,
    TGetCustomerStatusUtilReturn,
    TGetMerchantsPhoneNumber,
    TJoinCustomerUtilReturn,
    TPostPhoneVerificationUtilReturn,
    TPostVerifyPhoneUtilReturn,
    TUpdateCustomerUtilReturn,
    UpdateCustomerPayload,
    VerifyPhonePayload,
} from './customers.types';
import { ErrorMessages } from 'shared/constants/errorMessages';
import { logger } from 'shared/utils/logger/logger';
import { logReturnedError } from 'shared/utils/logger/logReturnedError';

const handleNetworkError = <T>(fallback: T): T => fallback;

const isCustomerStatusError = (
    error: unknown
): error is NonNullable<TGetCustomerStatusUtilReturn['error']> => {
    return error !== null && typeof error === 'object' && 'generic' in error;
};

const customerStatusErrorForLog = (
    error: NonNullable<TGetCustomerStatusUtilReturn['error']>
): unknown => {
    if (error.generic === ErrorMessages.MERCHANT_LOCATION_UNAVAILABLE) {
        return 'customer_merchant_exception';
    }

    return error;
};

export const getCustomerStatus = async (
    ...args: Parameters<GetCustomerStatus>
): Promise<TGetCustomerStatusUtilReturn> => {
    const [{ merchantId, email, phone_number }] = args;
    const query = new URLSearchParams({ merchant_id: merchantId });
    if (email) {
        query.append('email', email);
    }
    if (phone_number) {
        query.append('phone_number', phone_number);
    }

    try {
        const response = await fetch(`/api/v2/customers/status?${query}`);
        const res =
            (await response.json()) as Awaited<TGetCustomerStatusUtilReturn>;

        if (!response.ok) {
            if (res.error) {
                const structuredError = isCustomerStatusError(res.error)
                    ? res.error
                    : {
                          email: '',
                          phone_number: '',
                          generic:
                              response.status >= 500
                                  ? ErrorMessages.NETWORK_ISSUE
                                  : ErrorMessages.DEFAULT,
                      };
                logReturnedError(
                    'Customer status request returned error',
                    'Client:getCustomerStatus',
                    {
                        merchantId,
                        status: response.status,
                        hasEmail: Boolean(email),
                        hasPhoneNumber: Boolean(phone_number),
                        error: isCustomerStatusError(res.error)
                            ? customerStatusErrorForLog(res.error)
                            : res.error,
                    }
                );
                return { error: structuredError, data: null };
            }

            logger.warn(
                'Customer status request failed',
                'Client:getCustomerStatus',
                {
                    merchantId,
                    status: response.status,
                    hasEmail: Boolean(email),
                    hasPhoneNumber: Boolean(phone_number),
                }
            );
            return handleNetworkError({
                error: {
                    email: '',
                    phone_number: '',
                    generic:
                        response.status >= 500
                            ? ErrorMessages.NETWORK_ISSUE
                            : ErrorMessages.DEFAULT,
                },
                data: null,
            });
        }

        if (res.error) {
            logReturnedError(
                'Customer status request returned error',
                'Client:getCustomerStatus',
                {
                    merchantId,
                    hasEmail: Boolean(email),
                    hasPhoneNumber: Boolean(phone_number),
                    error: customerStatusErrorForLog(res.error),
                }
            );
        }

        return res;
    } catch (error) {
        logger.error(
            'Customer status request threw',
            'Client:getCustomerStatus',
            {
                merchantId,
                hasEmail: Boolean(email),
                hasPhoneNumber: Boolean(phone_number),
                error,
            }
        );
        return handleNetworkError({
            error: {
                email: '',
                phone_number: '',
                generic: ErrorMessages.NETWORK_ISSUE,
            },
            data: null,
        });
    }
};

export const createCustomer = async (
    payload: CreateCustomerPayload
): Promise<TCreateCustomerUtilReturn> => {
    try {
        const response = await fetch('/api/v2/customers/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            logger.warn(
                'Create customer request failed',
                'Client:createCustomer',
                {
                    merchantId: payload.merchant_id,
                    status: response.status,
                }
            );
            return handleNetworkError({
                error: {
                    full_name: '',
                    birthday: '',
                    phone_number: '',
                    email: '',
                    generic: ErrorMessages.NETWORK_ISSUE,
                },
                data: null,
            });
        }
        const res = await response.json();
        return res as Awaited<TCreateCustomerUtilReturn>;
    } catch (error) {
        logger.error('Create customer request threw', 'Client:createCustomer', {
            merchantId: payload.merchant_id,
            error,
        });
        return handleNetworkError({
            error: {
                full_name: '',
                birthday: '',
                phone_number: '',
                email: '',
                generic: ErrorMessages.NETWORK_ISSUE,
            },
            data: null,
        });
    }
};

export const joinCustomer = async (
    customerId: string,
    payload: JoinCustomerPayload
): Promise<TJoinCustomerUtilReturn> => {
    try {
        const response = await fetch(`/api/v2/customers/${customerId}/join`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const res = (await response.json()) as Awaited<TJoinCustomerUtilReturn>;
        if (res.error) {
            logReturnedError(
                'Join customer request returned error',
                'Client:joinCustomer',
                {
                    customerId,
                    merchantId: payload.merchant_id,
                    error: res.error,
                }
            );
        }
        return res;
    } catch (error) {
        logger.error('Join customer request threw', 'Client:joinCustomer', {
            customerId,
            merchantId: payload.merchant_id,
            error,
        });
        return handleNetworkError({ error: ErrorMessages.NETWORK_ISSUE });
    }
};

export const updateCustomer = async (
    customerId: string,
    payload: UpdateCustomerPayload
): Promise<TUpdateCustomerUtilReturn> => {
    try {
        const response = await fetch(`/api/v2/customers/${customerId}/update`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const res =
            (await response.json()) as Awaited<TUpdateCustomerUtilReturn>;
        if (res.error) {
            logger.warn(
                'Update customer request returned error',
                'Client:updateCustomer',
                {
                    customerId,
                    errorFields: Object.keys(res.error),
                }
            );
        }
        return res;
    } catch (error) {
        logger.error('Update customer request threw', 'Client:updateCustomer', {
            customerId,
            error,
        });
        return handleNetworkError({
            error: {
                email: '',
                full_name: '',
                birthday: '',
                phone_number: '',
                generic: ErrorMessages.NETWORK_ISSUE,
            },
        });
    }
};

export const sendPhoneVerification = async (
    phone_number: string
): Promise<TPostPhoneVerificationUtilReturn> => {
    try {
        const response = await fetch(
            '/api/v2/customers/phone-verification/send',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number }),
            }
        );
        const res =
            (await response.json()) as Awaited<TPostPhoneVerificationUtilReturn>;
        if (res.error) {
            logReturnedError(
                'Send phone verification request returned error',
                'Client:sendPhoneVerification',
                {
                    error: res.error,
                }
            );
        }
        return res;
    } catch (error) {
        logger.error(
            'Send phone verification request threw',
            'Client:sendPhoneVerification',
            {
                error,
            }
        );
        return handleNetworkError({
            error: ErrorMessages.NETWORK_ISSUE,
        });
    }
};

export const verifyPhone = async (
    payload: VerifyPhonePayload
): Promise<TPostVerifyPhoneUtilReturn> => {
    try {
        const response = await fetch(
            '/api/v2/customers/phone-verification/verify',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }
        );
        const res =
            (await response.json()) as Awaited<TPostVerifyPhoneUtilReturn>;
        if (res.error) {
            logReturnedError(
                'Verify phone request returned error',
                'Client:verifyPhone',
                {
                    error: res.error,
                }
            );
        }
        return res;
    } catch (error) {
        logger.error('Verify phone request threw', 'Client:verifyPhone', {
            error,
        });
        return handleNetworkError({
            error: ErrorMessages.NETWORK_ISSUE,
        });
    }
};

export const getMerchantsPhoneNumber =
    async (): Promise<TGetMerchantsPhoneNumber> => {
        try {
            const response = await fetch(
                '/api/v2/customers/phone-verification/phone-number'
            );
            const res =
                (await response.json()) as Awaited<TGetMerchantsPhoneNumber>;
            if (res.error) {
                logReturnedError(
                    'Get merchant phone number request returned error',
                    'Client:getMerchantsPhoneNumber',
                    {
                        error: res.error,
                    }
                );
            }
            return res;
        } catch (error) {
            logger.error(
                'Get merchant phone number request threw',
                'Client:getMerchantsPhoneNumber',
                {
                    error,
                }
            );
            return handleNetworkError({
                data: null,
                error: ErrorMessages.NETWORK_ISSUE,
            });
        }
    };
